import type { ReactNode } from 'react';
import { ImageDropZone } from '@/components/profile/image-drop-zone';
import type { ProfileImage } from '@/components/profile/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { uploadPhaseLabel, useImageUpload } from '@/hooks/use-image-upload';
import { cn } from '@/lib/utils';

export interface SkinGalleryProps {
  images: readonly ProfileImage[];
  /** `users.selected_skin_image_id`; null is the default circle, which is a choice too. */
  selectedSkinImageId: string | null;
  maxSkinImages: number;
  isLoading: boolean;
  /** True while a select or delete is in flight — both are one-at-a-time actions. */
  isBusy: boolean;
  actionError: string | null;
  onSelect: (imageId: string | null) => void;
  onDelete: (imageId: string) => void;
}

export function SkinGallery({
  images,
  selectedSkinImageId,
  maxSkinImages,
  isLoading,
  isBusy,
  actionError,
  onSelect,
  onDelete,
}: SkinGalleryProps) {
  const uploader = useImageUpload('skin');
  const status = uploadPhaseLabel(uploader.phase);
  const isFull = images.length >= maxSkinImages;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Circle photos</CardTitle>
        <CardDescription>
          Pick one to play as. {images.length} of {maxSkinImages} used. A moderator checks a new
          photo before anybody else sees it — you can pick one that is still waiting, it just shows
          as a plain circle to everyone else until then.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status && (
          <p className="text-sm font-medium" role="status">
            {status}
          </p>
        )}
        {uploader.error && (
          <p className="text-sm text-destructive" role="alert">
            {uploader.error}
          </p>
        )}
        {actionError && (
          <p className="text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading your photos…</p>
        ) : (
          <>
            {images.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No photos yet. Upload one and you will play as it instead of a plain circle.
              </p>
            )}

            <ul className="flex flex-wrap gap-3">
              <li>
                <Tile
                  label="Play as the default circle"
                  selected={selectedSkinImageId === null}
                  disabled={isBusy}
                  onSelect={() => onSelect(null)}
                >
                  <span className="flex size-full items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-muted-foreground">
                    Default
                  </span>
                </Tile>
              </li>

              {images.map((image, index) => (
                <li key={image.id}>
                  <Tile
                    label={`Play as photo ${index + 1}`}
                    selected={selectedSkinImageId === image.id}
                    // A rejected photo cannot be played as — `selectSkin` refuses it — so the
                    // control that would ask says no here rather than lying and being refused.
                    // A pending one is offered: the choice is theirs, it simply does not travel
                    // until a moderator agrees.
                    disabled={isBusy || image.status === 'rejected'}
                    onSelect={() => onSelect(image.id)}
                    onDelete={() => onDelete(image.id)}
                    deleteLabel={`Delete photo ${index + 1}`}
                  >
                    {image.status === 'rejected' ? (
                      // Nothing to draw: the bytes were deleted from the bucket when the verdict
                      // was recorded, so an <img> here would be a broken one.
                      <span className="flex size-full items-center justify-center rounded-full bg-muted text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Not approved
                      </span>
                    ) : (
                      <img src={image.url} alt="" className="size-full rounded-full object-cover" />
                    )}
                  </Tile>
                  {image.status !== 'approved' && (
                    <p className="mt-4 w-24 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {image.status === 'pending' ? 'Pending review' : 'Not approved'}
                    </p>
                  )}
                </li>
              ))}

              <li>
                <ImageDropZone
                  id="skin"
                  label="Upload a circle photo"
                  disabled={uploader.isUploading || isFull}
                  onFile={uploader.upload}
                  className="size-24 rounded-full p-2 leading-tight"
                >
                  <span>{isFull ? 'Library full' : 'Add a photo'}</span>
                </ImageDropZone>
              </li>
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One choosable circle. The delete control is a sibling of the select button rather than a child
 * of it — a button inside a button is invalid, and screen readers only reach the outer one.
 */
function Tile({
  label,
  selected,
  disabled,
  onSelect,
  onDelete,
  deleteLabel,
  children,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          'size-24 cursor-pointer overflow-hidden rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
          selected ? 'border-primary' : 'border-transparent hover:border-input',
        )}
      >
        {children}
      </button>

      {selected && (
        <span className="pointer-events-none absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
          Playing
        </span>
      )}

      {onDelete && deleteLabel && (
        <button
          type="button"
          aria-label={deleteLabel}
          disabled={disabled}
          onClick={onDelete}
          className="absolute -right-1 -top-1 flex size-6 cursor-pointer items-center justify-center rounded-full bg-destructive text-xs font-bold text-destructive-foreground opacity-80 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          ×
        </button>
      )}
    </div>
  );
}
